import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "../header";
import Footer from "../footer";
import { generateBreadcrumbJsonLd } from "../utils/breadcrumbJsonLd";
import {
  Home,
  ChevronRight,
  ArrowRight,
  FileText,
  BookOpen,
  Scale,
  Store,
  Zap,
  Gift,
  Shield,
  Lightbulb,
  Gavel,
  TrendingUp,
  Package,
  Rocket,
  Ticket,
} from "lucide-react";

type BlogPost = {
  slug: string;
  title: string;
  description: string;
  category: string;
  publishedAt: string;
  icon: React.ReactNode;
};

const blogPosts: BlogPost[] = [
  {
    slug: "how-to-create-kuji",
    title: "一番くじの作り方｜オンラインくじを無料で作成する方法",
    description:
      "一番くじ風のオンラインくじを無料で作る方法を5ステップで解説。飲食店・小売店・イベントでのくじ販売の始め方がわかります。",
    category: "ガイド",
    publishedAt: "2026年3月9日",
    icon: <Ticket className="w-6 h-6" />,
  },
  {
    slug: "how-to-start-oripa-business",
    title: "オリパサイトの始め方｜開業準備から販売開始までの全手順",
    description:
      "オリパサイトの始め方を9ステップで解説。古物商許可証の取得、サイト構築、仕入れ、集客まで、開業に必要な準備と手順をまとめました。",
    category: "始め方",
    publishedAt: "2025年12月16日",
    icon: <Rocket className="w-6 h-6" />,
  },
  {
    slug: "oripa-sourcing-guide",
    title: "オリパ仕入れ完全ガイド｜カード仕入れのルート・方法・注意点",
    description:
      "オリパ販売に必要なカード仕入れの方法を解説。問屋・フリマ・オークションなど仕入れルートの特徴、真贋判定、在庫管理のポイントをまとめました。",
    category: "運営ノウハウ",
    publishedAt: "2025年12月16日",
    icon: <Package className="w-6 h-6" />,
  },
  {
    slug: "oripa-market-trends-2026",
    title: "2026年オリパ市場トレンド予測｜急成長するトレカ市場の今後",
    description:
      "トレカ市場3,000億円突破の成長背景と、2026年のオリパ市場で予想される5大トレンドを解説します。",
    category: "市場分析",
    publishedAt: "2025年12月15日",
    icon: <TrendingUp className="w-6 h-6" />,
  },
  {
    slug: "oripa-legal-guide",
    title: "オリパ運営で失敗しないための法的注意点【2025年最新】",
    description:
      "オリパ運営に必要な法的知識を解説。古物商許可、景品表示法、特定商取引法など、運営者が押さえるべきポイントをまとめました。",
    category: "法律・規制",
    publishedAt: "2025年12月15日",
    icon: <Gavel className="w-6 h-6" />,
  },
  {
    slug: "how-to-create-oripa-site",
    title: "オリパの作り方｜サイト構築3つの方法を徹底比較",
    description:
      "オリパの作り方を初心者向けに解説。ECプラットフォーム、独自開発、オリパ特化SaaSの3つの方法を費用・機能・難易度で比較します。",
    category: "ガイド",
    publishedAt: "2025年12月1日",
    icon: <Lightbulb className="w-6 h-6" />,
  },
  {
    slug: "kobutsusho-guide",
    title: "古物商許可証の取り方完全ガイド",
    description:
      "オリパ販売・カード販売を始めるために必要な古物商許可証。取得の流れ・必要書類・費用まで詳しく解説します。",
    category: "ガイド",
    publishedAt: "2025年12月1日",
    icon: <FileText className="w-6 h-6" />,
  },
  {
    slug: "how-to-start",
    title: "JoyBaseでオリパサイトを始める方法",
    description:
      "JoyBaseを使えば簡単にオリパサイトを開設できます。サイト開設から運営までの流れを9ステップで解説。",
    category: "始め方",
    publishedAt: "2025年12月1日",
    icon: <BookOpen className="w-6 h-6" />,
  },
];

const BlogIndexPage: React.FC = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>ブログ | JoyBase - オリパサイト作成プラットフォーム</title>
        <meta
          name="description"
          content="JoyBaseのブログでは、オリパ販売に役立つ情報を発信しています。古物商許可証の取り方、オリパサイトの始め方など。"
        />
        <meta
          name="keywords"
          content="オリパ,オリパサイト,古物商許可証,トレーディングカード,JoyBase"
        />
        <meta
          property="og:title"
          content="ブログ | JoyBase - オリパサイト作成プラットフォーム"
        />
        <meta
          property="og:description"
          content="JoyBaseのブログでは、オリパ販売に役立つ情報を発信しています。"
        />
        <meta property="og:url" content="https://joybase.jp/blog" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://joybase.jp/blog" />
        <script type="application/ld+json">
        {generateBreadcrumbJsonLd([
          { name: "ホーム", url: "https://www.joybase.jp/" },
          { name: "ブログ", url: "https://www.joybase.jp/blog" }
        ])}
        </script>
      </Helmet>

      <Header />

      {/* Breadcrumbs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center text-sm text-gray-500">
            <Link to="/" className="hover:text-blue-600 flex items-center">
              <Home className="w-4 h-4" />
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-gray-700">ブログ</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-12 md:py-16">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
            ブログ
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            オリパ販売を始めるために役立つ情報をお届けします
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Column - Blog Posts */}
            <div className="flex-1 min-w-0">
              <div className="grid gap-6">
                {blogPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="block bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6"
                  >
                    <div className="flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-yellow-100 text-yellow-600 rounded-lg flex items-center justify-center">
                          {post.icon}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded">
                            {post.category}
                          </span>
                          <span className="text-xs text-gray-500">
                            {post.publishedAt}
                          </span>
                        </div>
                        <h2 className="text-lg md:text-xl font-bold text-gray-900 group-hover:text-blue-600">
                          {post.title}
                        </h2>
                        <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                          {post.description}
                        </p>
                        <div className="mt-3 flex items-center text-blue-600 text-sm font-medium">
                          続きを読む
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="lg:w-80 flex-shrink-0">
              <div className="sticky top-8 space-y-6">
                {/* Store Registration CTA */}
                <div className="rounded-xl shadow-lg overflow-hidden">
                  <div className="bg-yellow-400 px-5 py-3">
                    <div className="flex items-center gap-2 text-gray-900">
                      <Store className="w-4 h-4" />
                      <span className="text-sm font-bold">店舗オーナー様へ</span>
                    </div>
                  </div>
                  <div className="bg-white p-5">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      オリパサイトを無料で開設
                    </h3>
                    <p className="text-sm text-gray-600 mb-4">
                      初期費用・月額費用{" "}
                      <span className="font-bold text-yellow-600">0円</span>
                    </p>

                    <div className="space-y-2 mb-5 text-sm text-gray-700">
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-yellow-500" />
                        <span>10連・100連ガチャ</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-yellow-500" />
                        <span>ラストワン賞・キリ番賞</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-yellow-500" />
                        <span>SMS認証・年齢確認</span>
                      </div>
                    </div>

                    <a
                      href="https://dashboard.joybase.jp/signup"
                      className="flex items-center justify-center w-full px-4 py-3 bg-yellow-400 text-gray-900 font-bold rounded-lg hover:bg-yellow-500 transition-colors"
                    >
                      無料で店舗登録
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                </div>

                {/* Contact */}
                <div className="bg-gray-100 rounded-xl p-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-2">
                    お問い合わせ
                  </h3>
                  <p className="text-xs text-gray-600 mb-3">
                    JoyBaseの導入に関するご質問はお気軽にどうぞ
                  </p>
                  <a
                    href="mailto:support@joybase.jp"
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    support@joybase.jp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogIndexPage;
